import { googleMetrics, popIndexMetrics, tiktokMetrics } from './metrics';
import { useEffect } from 'react';
import { useFilterStateOnly } from 'utils/hooks/useFilter';
import useFlavorsByCategory from './useFlavorsByCategory';
import useHandleSearchInputChange from 'utils/hooks/useHandleSearchInputChange';
import { useLocation } from 'react-router';
import { useSortStateOnly } from 'utils/hooks/useSort';
import useTableColumns from 'utils/hooks/useTableColumns';

function getMetricsByDataSource(dataSource) {
    if (dataSource === 'google') return googleMetrics;
    if (dataSource === 'tiktok') return tiktokMetrics;
    if (dataSource === 'pop_index') return popIndexMetrics;
    throw new Error(`Unsupported data source: ${dataSource}`);
}

function getInitialSortOption(dataSource) {
    const metrics = getMetricsByDataSource(dataSource);
    return metrics[0][0].getSortOption({ ascending: false });
}

const DEFAULT_INITIAL_FILTER_OPTION = {};

function useFlavorsTableProps({
    category,
    country,
    dataSource,
    name,
    getColumnsConfig,
}) {
    const initialSortOption = getInitialSortOption(dataSource);
    const initialFilterOption = DEFAULT_INITIAL_FILTER_OPTION;
    const { filters, filtersKey, setFilter, setFilters, resetFilters } =
        useFilterStateOnly(initialFilterOption);
    const nameFilter =
        filters && filters.name && filters.name.length ? filters.name[0] : null;

    const handleSearchInputChange = useHandleSearchInputChange({
        nameFilter,
        setFilter,
    });
    const {
        setSortOption: baseSetSortOption,
        sortOption,
        sortOptionKey,
    } = useSortStateOnly(initialSortOption);
    const initialQueryData = {
        category: category ? [category] : null,
        country: country ? [country] : null,
        dataSource,
    };
    const queryData = {
        ...initialQueryData,
        enabled: Boolean(category && country && dataSource),
    };
    const queryDataFilterAndSortConfig = {
        order_by: sortOption?.sortBy,
        ascending: sortOption?.ascending,
        ...(filters?.flavorGroups?.length && {
            group: filters.flavorGroups.map(({ value }) =>
                value.split('||').pop(),
            ),
        }),
        ...(filters?.name?.length && { search: filters.name[0] }),
    };
    const { query, getQueryData } = useFlavorsByCategory({
        ...queryDataFilterAndSortConfig,
        ...queryData,
    });
    const {
        fetchNextPage,
        isFetchingNextPage,
        fetchPreviousPage,
        hasNextPage,
        hasPreviousPage,
        isFetching,
        ...result
    } = query;
    const rows = (result.data?.pages || []).reduce((acc, { data }) => {
        if (!data) return acc;
        acc.push(...data.map((o) => ({ ...o, relatedName: name })));
        return acc;
    }, []);
    const isSuccess = result.isSuccess;
    const isLoading = result.isLoading || result.isFetchingNextPage;
    const dataReady = isSuccess && rows.length > 0;

    const { visibleOrderedMetrics, columnsMenu, setSortOption } =
        useTableColumns({
            tableName: `flavors-${dataSource}-${category}-${country}`,
            metrics: getMetricsByDataSource(dataSource),
            setSortOption: baseSetSortOption,
        });

    const visibleColumnsConfig = getColumnsConfig(visibleOrderedMetrics);

    const isNoData = !(rows && rows.length);
    let noDataMessage;

    if (!isNoData && isSuccess) {
        noDataMessage = `No data to display. ${
            filters && Object.keys(filters).length
                ? 'Try adjusting the filters'
                : ''
        }`;
    } else if (isNoData && isLoading) {
        noDataMessage = 'Loading...';
    } else if (isNoData && result.isError) {
        noDataMessage =
            'Could not load the data. Refresh the page to try again.';
    } else if (isNoData) {
        noDataMessage = `No flavors for this category.`;
    }

    const { pathname } = useLocation();
    useEffect(() => {
        resetFilters();
        setSortOption(initialSortOption);
    }, [pathname]);

    return {
        columnsMenu,
        dataReady,
        isLoading,
        noDataMessage,
        rows,
        sortedData: rows,
        filters,
        filtersKey,
        setFilter,
        setFilters,
        resetFilters,
        sortOption,
        setSortOption,
        sortOptionKey,
        visibleColumnsConfig,
        handleSearchInputChange,
        queryData,
        fetchNextPage,
        isFetchingNextPage,
        isFetching,
        pages: result.data?.pages,
        getQueryData,
        initialSortOption,
    };
}

export default useFlavorsTableProps;
